import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountryPicker } from "@/components/CountryPicker";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { IndustryPicker, type Industry } from "@/components/IndustryPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type Organisation = components["schemas"]["OrganisationResponse"];
type CountrySummary = components["schemas"]["CountryBankProfileSummary"];
type Currency = components["schemas"]["CurrencyResponse"];

const businessTypeLabels: Record<string, string> = {
  SoleTrader: "Sole Trader",
  LimitedCompany: "Limited Company",
  LLP: "LLP",
  Partnership: "Partnership",
};

const employeeCountLabels: Record<string, string> = {
  OneToTen: "1\u201310",
  ElevenToFifty: "11\u201350",
  FiftyOneToTwoHundred: "51\u2013200",
  TwoHundredToOneThousand: "200\u20131,000",
  OneThousandPlus: "1,000+",
};

const annualRevenueLabels: Record<string, string> = {
  Under100k: "Under \u00a3100k",
  OneHundredToFiveHundredK: "\u00a3100k\u2013\u00a3500k",
  FiveHundredKToTwoM: "\u00a3500k\u2013\u00a32M",
  TwoMPlus: "\u00a32M+",
};

const avgInvoicesPerMonthLabels: Record<string, string> = {
  OneToTen: "1\u201310",
  ElevenToFifty: "11\u201350",
  FiftyToOneHundred: "50\u2013100",
  OneHundredPlus: "100+",
};

const avgInvoiceValueLabels: Record<string, string> = {
  Under500: "Under \u00a3500",
  FiveHundredToFiveK: "\u00a3500\u2013\u00a35k",
  FiveKToFiftyK: "\u00a35k\u2013\u00a350k",
  Over50k: "Over \u00a350k",
};

const primaryPaymentMethodLabels: Record<string, string> = {
  BankTransfer: "Bank Transfer",
  Card: "Card",
  DirectDebit: "Direct Debit",
  Cash: "Cash",
};

function labelFrom(map: Record<string, string>, value: string): string {
  return map[value] ?? value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

interface EditOrganisationDialogProps {
  organisation: Organisation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: () => void;
}

export function EditOrganisationDialog({
  organisation,
  open,
  onOpenChange,
  onUpdated,
}: EditOrganisationDialogProps) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [companyRegistrationNumber, setCompanyRegistrationNumber] = useState("");
  const [vatRegistered, setVatRegistered] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [incorporationYear, setIncorporationYear] = useState("");
  const [employeeCount, setEmployeeCount] = useState("");
  const [annualRevenue, setAnnualRevenue] = useState("");
  const [avgInvoicesPerMonth, setAvgInvoicesPerMonth] = useState("");
  const [avgInvoiceValue, setAvgInvoiceValue] = useState("");
  const [primaryPaymentMethod, setPrimaryPaymentMethod] = useState("");
  const [primaryColour, setPrimaryColour] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [supportedCountries, setSupportedCountries] = useState<CountrySummary[]>([]);
  const countriesFetched = useRef(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const currenciesFetched = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || countriesFetched.current) return;
    async function fetchCountries() {
      const { data } = await api.GET("/v1/bank-details/countries");
      if (data) setSupportedCountries(data);
      countriesFetched.current = true;
    }
    fetchCountries();
  }, [open]);

  useEffect(() => {
    if (!open || currenciesFetched.current) return;
    async function fetchCurrencies() {
      const { data } = await api.GET("/v1/currencies/available");
      if (data) setCurrencies(data);
      currenciesFetched.current = true;
    }
    fetchCurrencies();
  }, [open]);

  useEffect(() => {
    if (organisation) {
      setName(organisation.name ?? "");
      setContactEmail(organisation.contactEmail ?? "");
      setPhone(organisation.phone ?? "");
      setWebsite(organisation.website ?? "");
      setAddressLine1(organisation.addressLine1 ?? "");
      setAddressLine2(organisation.addressLine2 ?? "");
      setCity(organisation.city ?? "");
      setPostalCode(organisation.postalCode ?? "");
      setCountry(organisation.country ?? "");
      setTaxNumber(organisation.taxNumber ?? "");
      setCompanyRegistrationNumber(organisation.companyRegistrationNumber ?? "");
      setVatRegistered(organisation.vatRegistered != null ? String(organisation.vatRegistered) : "");
      setBusinessType(organisation.businessType ?? "");
      setIncorporationYear(organisation.incorporationYear != null ? String(organisation.incorporationYear) : "");
      setEmployeeCount(organisation.employeeCount ?? "");
      setAnnualRevenue(organisation.annualRevenue ?? "");
      setAvgInvoicesPerMonth(organisation.avgInvoicesPerMonth ?? "");
      setAvgInvoiceValue(organisation.avgInvoiceValue ?? "");
      setPrimaryPaymentMethod(organisation.primaryPaymentMethod ?? "");
      setPrimaryColour(organisation.primaryColour ?? "");
      setLogoUrl(organisation.logoUrl ?? "");
      setDefaultCurrency(organisation.defaultCurrency ?? "");
      setSelectedIndustry(
        organisation.industryId
          ? {
              id: organisation.industryId,
              code: organisation.industryCode ?? "",
              displayName: organisation.industryDisplayName ?? "",
              slug: "",
            }
          : null,
      );
      setError(null);
    }
  }, [organisation]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSubmitting(false);
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!organisation) return;
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!selectedIndustry?.id) {
      setError("Please select an industry.");
      return;
    }
    if (!contactEmail.trim()) {
      setError("Contact email is required.");
      return;
    }
    if (!country.trim()) {
      setError("Country is required.");
      return;
    }

    setSubmitting(true);

    const { error: apiError, response } = await api.PUT("/v1/organisations/{id}", {
      params: { path: { id: organisation.id! } },
      body: {
        name: name.trim(),
        industryId: selectedIndustry.id,
        contactEmail: contactEmail.trim(),
        phone: phone.trim() || null,
        website: website.trim() || null,
        addressLine1: addressLine1.trim() || null,
        addressLine2: addressLine2.trim() || null,
        city: city.trim() || null,
        postalCode: postalCode.trim() || null,
        country: country.trim().toUpperCase(),
        taxNumber: taxNumber.trim() || null,
        companyRegistrationNumber: companyRegistrationNumber.trim() || null,
        vatRegistered: vatRegistered === "true" ? true : vatRegistered === "false" ? false : null,
        businessType: (businessType as "SoleTrader" | "LimitedCompany" | "LLP" | "Partnership") || null,
        incorporationYear: incorporationYear ? parseInt(incorporationYear, 10) : null,
        employeeCount: (employeeCount as "OneToTen" | "ElevenToFifty" | "FiftyOneToTwoHundred" | "TwoHundredToOneThousand" | "OneThousandPlus") || null,
        annualRevenue: (annualRevenue as "Under100k" | "OneHundredToFiveHundredK" | "FiveHundredKToTwoM" | "TwoMPlus") || null,
        avgInvoicesPerMonth: (avgInvoicesPerMonth as "OneToTen" | "ElevenToFifty" | "FiftyToOneHundred" | "OneHundredPlus") || null,
        avgInvoiceValue: (avgInvoiceValue as "Under500" | "FiveHundredToFiveK" | "FiveKToFiftyK" | "Over50k") || null,
        primaryPaymentMethod: (primaryPaymentMethod as "BankTransfer" | "Card" | "DirectDebit" | "Cash") || null,
        primaryColour: primaryColour.trim() || null,
        logoUrl: logoUrl.trim() || null,
        defaultCurrency: defaultCurrency.trim().toUpperCase() || null,
      },
    });

    if (apiError) {
      setError(apiError.detail || apiError.title || "Failed to update organisation.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onUpdated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Organisation</DialogTitle>
            <DialogDescription>Update organisation details.</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            {/* Business Info */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Business Info</Label>
            <div className="space-y-2">
              <Label htmlFor="edit-org-name">Name</Label>
              <Input id="edit-org-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Type</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(businessTypeLabels).map((v) => (
                      <SelectItem key={v} value={v}>{labelFrom(businessTypeLabels, v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-org-inc-year">Incorporation Year</Label>
                <Input id="edit-org-inc-year" type="number" placeholder="2020" value={incorporationYear} onChange={(e) => setIncorporationYear(e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-org-industry">Industry</Label>
              <IndustryPicker
                id="edit-org-industry"
                value={selectedIndustry}
                onChange={setSelectedIndustry}
              />
            </div>

            <div className="space-y-2">
              <Label>Employee Count</Label>
              <Select value={employeeCount} onValueChange={setEmployeeCount}>
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(employeeCountLabels).map((v) => (
                    <SelectItem key={v} value={v}>{labelFrom(employeeCountLabels, v)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-org-email">Contact Email <span className="text-destructive">*</span></Label>
                <Input id="edit-org-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-org-phone">Phone</Label>
                <Input id="edit-org-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-org-website">Website</Label>
              <Input id="edit-org-website" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>VAT Registered?</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "flex-1",
                    vatRegistered === "true" && "border-teal-500 bg-teal-500/10 text-teal-500",
                  )}
                  onClick={() => setVatRegistered("true")}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "flex-1",
                    vatRegistered === "false" && "border-teal-500 bg-teal-500/10 text-teal-500",
                  )}
                  onClick={() => setVatRegistered("false")}
                >
                  No
                </Button>
              </div>
            </div>

            {/* Legal */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Legal / Tax</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-org-tax">VAT Number</Label>
                <Input id="edit-org-tax" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-org-reg">Company Reg.</Label>
                <Input id="edit-org-reg" value={companyRegistrationNumber} onChange={(e) => setCompanyRegistrationNumber(e.target.value)} />
              </div>
            </div>

            {/* Address */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Address</Label>
            <Input placeholder="Address line 1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
            <Input placeholder="Address line 2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
              <Input placeholder="Postal code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-org-country">Country <span className="text-destructive">*</span></Label>
              <CountryPicker
                id="edit-org-country"
                value={country}
                onChange={(code) => setCountry(code)}
                countries={supportedCountries}
              />
            </div>

            {/* Financial Profile */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Financial Profile</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Annual Revenue</Label>
                <Select value={annualRevenue} onValueChange={setAnnualRevenue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(annualRevenueLabels).map((v) => (
                      <SelectItem key={v} value={v}>{labelFrom(annualRevenueLabels, v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Invoices per Month</Label>
                <Select value={avgInvoicesPerMonth} onValueChange={setAvgInvoicesPerMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(avgInvoicesPerMonthLabels).map((v) => (
                      <SelectItem key={v} value={v}>{labelFrom(avgInvoicesPerMonthLabels, v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Average Invoice Value</Label>
                <Select value={avgInvoiceValue} onValueChange={setAvgInvoiceValue}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(avgInvoiceValueLabels).map((v) => (
                      <SelectItem key={v} value={v}>{labelFrom(avgInvoiceValueLabels, v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Primary Payment Method</Label>
                <Select value={primaryPaymentMethod} onValueChange={setPrimaryPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(primaryPaymentMethodLabels).map((v) => (
                      <SelectItem key={v} value={v}>{labelFrom(primaryPaymentMethodLabels, v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Branding */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Branding</Label>
            <div className="space-y-2">
              <Label htmlFor="edit-org-colour">Primary Colour</Label>
              <div className="flex items-center gap-3">
                <Input id="edit-org-colour" value={primaryColour} onChange={(e) => setPrimaryColour(e.target.value)} placeholder="#2DD4BF" maxLength={7} className="flex-1" />
                <label
                  className="relative h-9 w-9 shrink-0 cursor-pointer rounded-md border"
                  style={{
                    backgroundColor:
                      primaryColour && /^#[0-9A-Fa-f]{6}$/.test(primaryColour)
                        ? primaryColour
                        : "#ffffff",
                  }}
                >
                  <input
                    type="color"
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    value={
                      primaryColour && /^#[0-9A-Fa-f]{6}$/.test(primaryColour)
                        ? primaryColour
                        : "#ffffff"
                    }
                    onChange={(e) => setPrimaryColour(e.target.value.toUpperCase())}
                  />
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-org-logo">Logo URL</Label>
              <Input id="edit-org-logo" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
            </div>

            {/* Preferences */}
            <Label className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Preferences</Label>
            <div className="space-y-2">
              <Label>Default Currency</Label>
              <CurrencyPicker
                value={defaultCurrency}
                onChange={setDefaultCurrency}
                currencies={currencies}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Invoice defaults (payment terms, late fee, tone, reminders), the invoice-number series, and
              bank details are the organisation's own AR settings — view them on this page; the organisation
              edits them from its own dashboard.
            </p>

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

